import { useEffect } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { useNavigate } from "react-router-dom";

const formSchema = z.object({
  Timestamp: z.string(),
  Name: z.string().min(3, "Name must be at least 3 characters").max(50),
  "Current Address": z.string().min(5, "Address must be at least 5 characters"),
  "Contact Number": z
    .string()
    .min(10, "Contact number must be at least 10 digits")
    .max(15)
    .regex(/^\+?[\d\s-]{10,}$/, "Invalid phone number format"),
  "Whatsapp Number": z
    .string()
    .optional()
    .refine(
      (val) => !val || /^\+?[\d\s-]{10,}$/.test(val),
      "Invalid WhatsApp number format",
    ),
  Email: z.string().email("Invalid email address").optional().or(z.literal("")),
  "Tentative Budget": z
    .number({ invalid_type_error: "Budget must be a number" })
    .min(0)
    .max(1000000000),
  "Property Type": z.string().min(1, "Property Type is required"),
  Project: z.string().min(1, "Project is required"),
  "Unit Type": z.string().min(1, "Unit Type is required"),
});

type FormData = z.infer<typeof formSchema>;

const EnquireNow: React.FC = () => {
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    mode: "onChange",
    defaultValues: {
      Name: "",
      "Current Address": "",
      "Contact Number": "",
      "Whatsapp Number": "",
      Email: "",
      "Tentative Budget": undefined,
      "Property Type": "",
      Project: "",
      "Unit Type": "",
    },
  });

  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      }
    };

    window.addEventListener("hashchange", handleHashChange);
    handleHashChange();

    return () => {
      window.removeEventListener("hashchange", handleHashChange);
    };
  }, []);

  const onSubmit = async (data: FormData) => {
    try {
      const formData = new FormData();
      Object.entries(data).forEach(([key, value]) => {
        formData.append(key, value?.toString() || "");
      });
      formData.append("Timestamp", new Date().toISOString());

      const scriptURL =
        "https://script.google.com/macros/s/AKfycbwWCusrADKhfseYWDNg7iXJtWt0B_iCXEErN6Hclznk7ZpLh8znmlE5bGd4pIdqRKRdQw/exec";

      await fetch(scriptURL, { method: "POST", body: formData });
      toast.success("Form submitted successfully!");

      // Push custom event to dataLayer for GTM
      // @ts-ignore
      window.dataLayer = window.dataLayer || [];
      // @ts-ignore
      window.dataLayer.push({
        event: "form_submission",
        conversionValue: data["Tentative Budget"],
        currency: "INR",
      });

      setTimeout(() => reset(), 100);
      navigate("/thankyou");
    } catch (error) {
      console.error("Error in form submission:", error);
      toast.error("Failed to send enquiry. Please try again.");
    }
  };

  const getInputStatus = (field: keyof FormData) => {
    return errors[field]
      ? "border-red-500 focus:ring-red-500"
      : "border-gray-300 focus:ring-blue-500";
  };

  return (
    <div
      className="min-h-screen bg-white font-sans text-gray-800"
      style={{ fontFamily: "Gambetta" }}
    >
      <ToastContainer />
      <section
        className="relative flex min-h-[75vh] w-full items-center justify-center"
        style={{
          backgroundImage: "url('/assets/imgs/EnquireBG.jpg')",
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      >
        <div className="absolute inset-0 bg-black/50"></div>
        <div className="relative z-10 text-center text-white">
          <h1
            className="animate-fade-in text-4xl font-bold tracking-widest md:text-5xl"
            style={{ fontFamily: "HistoryPro" }}
          >
            Enquire Now
          </h1>
        </div>
      </section>
      <section
        id="contact-form"
        className="mx-auto flex min-h-screen max-w-2xl items-center justify-center px-6 py-16 lg:px-0"
      >
        <div className="w-full rounded-lg px-6 py-8 shadow-xl">
          <h1 className="mb-6 text-3xl font-semibold">Let’s Get in Touch</h1>
          <p className="mb-6 text-gray-600">
            Reach out to us today and let’s start building your dream together.
            Your satisfaction is our priority.
          </p>
          <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
            <div className="grid grid-cols-1 gap-4">
              <input
                type="hidden"
                {...register("Timestamp")}
                value={new Date().toLocaleString("en-IN", {
                  timeZone: "Asia/Kolkata",
                })}
              />
              <div>
                <label
                  htmlFor="name"
                  className="block text-sm font-medium text-gray-700"
                >
                  Name
                </label>
                <input
                  type="text"
                  id="name"
                  {...register("Name")}
                  placeholder="Name"
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Name")}`}
                />
                {errors.Name && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.Name.message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="currentAddress"
                  className="block text-sm font-medium text-gray-700"
                >
                  Current Address
                </label>
                <input
                  type="text"
                  id="currentAddress"
                  {...register("Current Address")}
                  placeholder="Current Address"
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Current Address")}`}
                />
                {errors["Current Address"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["Current Address"].message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="contactNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Contact Number
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  {...register("Contact Number")}
                  placeholder="Contact Number"
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Contact Number")}`}
                />
                {errors["Contact Number"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["Contact Number"].message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="whatsappNumber"
                  className="block text-sm font-medium text-gray-700"
                >
                  Whatsapp Number (Optional)
                </label>
                <input
                  type="tel"
                  id="whatsappNumber"
                  {...register("Whatsapp Number")}
                  placeholder="Whatsapp Number (Optional)"
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Whatsapp Number")}`}
                />
                {errors["Whatsapp Number"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["Whatsapp Number"].message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-gray-700"
                >
                  Email (Optional)
                </label>
                <input
                  type="email"
                  id="email"
                  {...register("Email")}
                  placeholder="Email (Optional)"
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Email")}`}
                />
                {errors.Email && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.Email.message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="tentativeBudget"
                  className="block text-sm font-medium text-gray-700"
                >
                  Tentative Budget
                </label>
                <input
                  type="number"
                  id="tentativeBudget"
                  {...register("Tentative Budget", { valueAsNumber: true })}
                  placeholder="Tentative Budget"
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Tentative Budget")}`}
                  onKeyDown={(e) => {
                    if (
                      !/[0-9]/.test(e.key) &&
                      e.key !== "Backspace" &&
                      e.key !== "Delete" &&
                      e.key !== "ArrowLeft" &&
                      e.key !== "ArrowRight" &&
                      e.key !== "Tab"
                    ) {
                      e.preventDefault();
                    }
                  }}
                />
                {errors["Tentative Budget"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["Tentative Budget"].message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="propertyType"
                  className="block text-sm font-medium text-gray-700"
                >
                  Property Type
                </label>
                <select
                  id="propertyType"
                  {...register("Property Type")}
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Property Type")}`}
                >
                  <option value="">Select Property Type</option>
                  <option value="Apartment">Apartment</option>
                  <option value="Villa">Villa</option>
                </select>
                {errors["Property Type"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["Property Type"].message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="project"
                  className="block text-sm font-medium text-gray-700"
                >
                  Project
                </label>
                <select
                  id="project"
                  {...register("Project")}
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Project")}`}
                >
                  <option value="">Select Project</option>
                  <option value="Nakshatra Nagar - Phase 3">
                    Nakshatra Nagar - Phase 3
                  </option>
                  <option value="Nakshatra Nagar - Phase 4">
                    Nakshatra Nagar - Phase 4
                  </option>
                </select>
                {errors.Project && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.Project.message}
                  </p>
                )}
              </div>
              <div>
                <label
                  htmlFor="unitType"
                  className="block text-sm font-medium text-gray-700"
                >
                  Unit Type
                </label>
                <select
                  id="unitType"
                  {...register("Unit Type")}
                  className={`mt-1 w-full rounded border p-2 focus:outline-none focus:ring-2 ${getInputStatus("Unit Type")}`}
                >
                  <option value="">Select Unit Type</option>
                  <option value="1 BHK">1 BHK</option>
                  <option value="2 BHK">2 BHK</option>
                  <option value="3 BHK">3 BHK</option>
                  <option value="4 BHK">4 BHK</option>
                </select>
                {errors["Unit Type"] && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors["Unit Type"].message}
                  </p>
                )}
              </div>
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`w-full rounded bg-blue-600 px-4 py-2 text-white ${
                isSubmitting
                  ? "cursor-not-allowed opacity-50"
                  : "hover:bg-blue-700"
              }`}
            >
              {isSubmitting ? "Submitting..." : "Submit"}
            </button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default EnquireNow;
